import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { SabreText } from './models/sabre-text';
import { ScreenSizeService } from '../../services/screen-size.service';
import { RequestState } from '../../models/request-state';

@Component({
  selector: 'app-sabre-text-dialog',
  standalone: false,
  templateUrl: './sabre-text-dialog.component.html',
  styleUrls: ['./sabre-text-dialog.component.scss'],
})
export class SabreTextDialogComponent implements OnInit, OnDestroy {
  sabreText$!: Observable<RequestState<SabreText>>;
  destroy$ = new Subject<void>();

  private readonly STD_CHARS_PER_LINE = 63;
  fontSizePx = 0;
  screenWidth = 0;
  maxLength = 0; // Max characters per line.

  constructor(
    private screenSizeService: ScreenSizeService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      sabreText$: Observable<RequestState<SabreText>>;
      refreshFn?: (request: unknown) => Observable<RequestState<SabreText>>;
      request?: unknown;
    },
  ) {}

  ngOnInit(): void {
    this.screenSizeService.screenWidth$
      .pipe(takeUntil(this.destroy$))
      .subscribe((screenWidth) => {
        this.screenWidth = screenWidth;
        this.updateFontSize();
      });

    // Add functionality to existing pipe to close dialog on error.
    this.sabreText$ = this.data.sabreText$.pipe(
      tap((sabreText) => {
        if (sabreText.response) {
          // Clamp max width to standard. Sometimes a Sabre glitch causes extra wide blank text.
          if (sabreText.response.maxLength > this.STD_CHARS_PER_LINE) {
            this.maxLength = this.STD_CHARS_PER_LINE;
          } else {
            this.maxLength = sabreText.response.maxLength;
          }
          this.updateFontSize();
        }
        // Automatically close dialog on error.
        if (sabreText.hasError || sabreText.isCompleteWithNoResponse()) {
          this.dialog.closeAll();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRefreshClick(): void {
    if (this.data.refreshFn && this.data.request) {
      this.sabreText$ = this.data.refreshFn(this.data.request).pipe(
        tap((sabreText) => {
          if (sabreText.response) {
            // Clamp max width to standard. Sometimes a Sabre glitch causes extra wide blank text.
            if (sabreText.response.maxLength > this.STD_CHARS_PER_LINE) {
              this.maxLength = this.STD_CHARS_PER_LINE;
            } else {
              this.maxLength = sabreText.response.maxLength;
            }
            this.updateFontSize();
          }
          // Automatically close dialog on error.
          if (sabreText.hasError || sabreText.isCompleteWithNoResponse()) {
            this.dialog.closeAll();
          }
        }),
      );
    }
  }

  onCloseDialog(): void {
    this.dialog.closeAll();
  }

  onFitToScreen(): void {
    this.updateFontSize();
  }

  onIncreaseFontSize(): void {
    if (this.fontSizePx < 17) {
      this.fontSizePx++;
    }
  }

  onDecreaseFontSize(): void {
    if (this.fontSizePx > 6) {
      this.fontSizePx--;
    }
  }

  // Calculate auto-fit font size based on screen width.
  updateFontSize(): void {
    const magicNumber = 0.51; // Constant to divide by to get optimal font size.
    const maxAutoFontSize = 16;
    const dialogMaxVw = 0.95; // Dialog max width as % vw.
    const paddingX = 16; // Mat dialog horizontal padding.

    const usableWidth = this.screenWidth * dialogMaxVw - paddingX * 2;
    this.fontSizePx = Math.min(
      usableWidth / this.maxLength / magicNumber,
      maxAutoFontSize,
    );
  }
}
